import { useMemo } from 'react'
import TurfCenterOfMass from '@turf/center-of-mass'

import { stackIds } from '../../containers/MapStackPresenter'
import { HighlightedNotamsState } from '../../states/HighlightedNotamsState'
import { MapGeoJsonLayer } from '../MapView/MapGeoJsonLayer'
import { MapGeoJsonSource } from '../MapView/MapGeoJsonSource'
import { MapSymbolLayer } from '../MapView/MapSymbolLayer'
import { notamHighlightColor, notamImage } from './MapHighlightedNotams.styles'

import { useBehaviorSubject } from '@/presentation/hooks/useBehaviorSubject'

function MapHighlightedNotams() {
  const highlightedNotamsState = useBehaviorSubject(HighlightedNotamsState)

  const notamsFeatureCollection = useMemo(() => {
    if (!highlightedNotamsState.collection.features || highlightedNotamsState.collection.features.length === 0) {
      return null
    }
    return highlightedNotamsState.collection
  }, [highlightedNotamsState.collection])

  const symbolFeatureCollection = useMemo(() => {
    if (!notamsFeatureCollection || notamsFeatureCollection.features.length === 0) {
      return null
    }

    const centroids = notamsFeatureCollection.features.map((feature) => {
      const centroid = TurfCenterOfMass(feature, {
        properties: {
          ...feature.properties,
          codeLabel: feature.properties.identifier
        }
      })
      return centroid
    })

    return {
      type: 'FeatureCollection' as const,
      features: centroids
    }
  }, [notamsFeatureCollection])

  if (!notamsFeatureCollection || !symbolFeatureCollection) {
    return null
  }

  return (
    <>
      <MapGeoJsonSource id="notams-highlighted-source" data={notamsFeatureCollection}>
        <MapGeoJsonLayer
          types={['fill', 'line']}
          id="notams-highlighted"
          sourceId="notams-highlighted-source"
          beforeLayerId={stackIds.notams}
          fillColor={notamHighlightColor}
          fillOpacity={0.4}
          borderColor={notamHighlightColor}
          borderWidth={['interpolate', ['linear'], ['zoom'], 0, 0.5, 10, 3, 12, 8, 22, 13]}
          layout={{
            'line-cap': 'round',
            'line-join': 'round'
          }}
          paint={{
            'line-dasharray': [4, 3]
          }}
        />
      </MapGeoJsonSource>

      <MapGeoJsonSource id="notams-highlighted-symbol-source" data={symbolFeatureCollection}>
        <MapSymbolLayer
          id="notams-highlighted-symbol"
          sourceId="notams-highlighted-symbol-source"
          beforeLayerId={stackIds.notamsLabel}
          imageSrc={notamImage}
          layout={{
            'text-field': ['get', 'codeLabel'],
            'text-size': 14,
            'text-font': ['Open Sans Bold'],
            'icon-text-fit': 'both',
            'icon-text-fit-padding': [3, 6, 3, 6],
            'icon-allow-overlap': true,
            'text-ignore-placement': true,
            'text-allow-overlap': true,
            'text-anchor': 'center'
          }}
          textColor="#f2f2f2"
          minZoomLevel={6}
        />
      </MapGeoJsonSource>
    </>
  )
}

export { MapHighlightedNotams }
